import * as React from "react";
import { css } from "@emotion/core";
import { NonIdealState } from "@blueprintjs/core";
import {
  Alert,
  Button,
  Checkbox,
  InputGroup,
  Tabs,
  Tab,
} from "@blueprintjs/core";
import {
  ColumnHeaderCell,
  Cell,
  Column,
  Table,
  Regions,
  RegionCardinality,
  RenderMode,
} from "@blueprintjs/table";
import { Spacer } from "components/common";
import { useSinkState } from "context/";
import MapPreview from "./MapPreview";

const respondOrError = (response) => {
  if (response.status === 404) throw new Error("Not Found");
  return response.json();
};

const CheckboxWithStopPropagation = ({ checked, setChecked, disabled }) => {
  const inputRef = React.useRef();

  React.useEffect(() => {
    const fn = (event) => {
      event.stopPropagation();
      setChecked((previous) => !previous);
    };
    const ref = inputRef.current;

    if (ref) {
      ref.addEventListener("click", fn);
      return () => ref.removeEventListener("click", fn);
    }
  }, [setChecked]);

  return (
    <Checkbox
      css={css`
        margin: 0;

        & > input:focus ~ span.bp6-control-indicator {
          outline: none;
        }
      `}
      inputRef={inputRef}
      checked={checked}
      onChange={() => null}
      disabled={disabled}
    />
  );
};

const FileTable = ({
  rows,
  checked,
  setChecked,
  markerSelection,
  removeImages,
  uploadInProgress,
}) => {
  const selectedIndex = React.useMemo(
    () => rows.findIndex((row) => markerSelection.isSelected(row.uid)),
    [rows, markerSelection]
  );

  const [confirmation, setConfirmation] = React.useState(false);

  const tableRef = React.useRef(null);
  const selectionRef = React.useRef(false);

  React.useEffect(() => {
    if (
      tableRef.current &&
      selectedIndex >= 0 &&
      selectionRef.current !== true
    ) {
      tableRef.current.scrollToRegion(Regions.cell(selectedIndex - 8, 0));
    }

    selectionRef.current = false;
  }, [selectedIndex]);

  if (rows.length === 0) return null;

  const cellRenderer = (key) => (rowIndex: number) =>
    (
      <Cell>
        <div>{rows[rowIndex][key]}</div>
      </Cell>
    );

  const statusRenderer = (rowIndex: number) => {
    if (rows[rowIndex].uploaded) return <Cell>UPLOADED</Cell>;
    if (rows[rowIndex].handle) return <Cell>PENDING UPLOAD</Cell>;

    return <Cell>MISSING</Cell>;
  };

  const actionRenderer = (rowIndex) => {
    const image = rows[rowIndex];

    return (
      <Cell
        css={css`
          & > div {
            overflow: visible;
          }
          display: flex !important;
          align-items: center;
        `}
      >
        <CheckboxWithStopPropagation
          checked={checked[image.uid] || false}
          setChecked={(value) => {
            if (typeof value === "function") {
              setChecked((previous) => ({
                ...previous,
                [image.uid]: value(previous[image.uid]),
              }));
            } else {
              setChecked((previous) => ({ ...previous, [image.uid]: value }));
            }
          }}
          disabled={uploadInProgress}
        />
      </Cell>
    );
  };

  const checkedRows = Object.values(checked);
  const allChecked =
    checkedRows.every((v) => v) && checkedRows.length === rows.length;
  const someChecked = checkedRows.some((v) => v);

  const headerCellRenderer = (label) => () => <ColumnHeaderCell name={label} />;
  const headerCheckboxRenderer = () => {
    return (
      <ColumnHeaderCell
        css={css`
          padding: 6px 0 0 10px;
        `}
      >
        <CheckboxWithStopPropagation
          checked={allChecked}
          setChecked={() => {
            setChecked((previous) => {
              const checkedRows = Object.values(previous);
              const allChecked =
                checkedRows.every((v) => v) &&
                checkedRows.length === rows.length;
              if (allChecked) {
                return {};
              } else {
                const checkedRecord = {};

                for (let index = 0; index < rows.length; index += 1) {
                  checkedRecord[rows[index].uid] = true;
                }

                return checkedRecord;
              }
            });
          }}
          disabled={uploadInProgress}
        />
      </ColumnHeaderCell>
    );
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding-right: 1rem;
      `}
    >
      <Alert
        className="bp6-dark"
        cancelButtonText="Cancel"
        confirmButtonText="Move to Trash"
        icon="trash"
        intent="danger"
        isOpen={confirmation}
        onCancel={() => {
          setConfirmation(false);
        }}
        onConfirm={() => {
          const local: any[] = [];
          const remote: any[] = [];

          for (let i = 0; i < rows.length; i++) {
            const image = rows[i];

            if (checked[image.uid]) {
              if (image.id) {
                remote.push(image);
              } else {
                local.push(image);
              }
            }
          }

          removeImages(local.map((image) => image.uid));

          if (remote.length > 0) {
            fetch("/api/v1/images", {
              method: "DELETE",
              headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ ids: remote.map((image) => image.id) }),
            })
              .then(respondOrError)
              .then((ids) => {
                removeImages(remote.map((image) => image.uid));
              });
          }

          setChecked({});

          markerSelection.reset();

          setConfirmation(false);
        }}
      >
        <p>
          Are you sure you want to delete {checkedRows.length} selected images?
        </p>
      </Alert>
      <Button
        icon="trash"
        type="button"
        intent={someChecked ? "danger" : "none"}
        disabled={!someChecked}
        onClick={() => setConfirmation(true)}
      >
        Delete Selected Images
      </Button>
      <Spacer />
      <Table
        ref={tableRef}
        numRows={rows.length}
        defaultRowHeight={22}
        columnWidths={[36, 260, 140]}
        enableMultipleSelection={false}
        enableColumnResizing={false}
        renderMode={RenderMode.NONE}
        selectedRegions={
          selectedIndex >= 0 ? [Regions.cell(selectedIndex, 1)] : []
        }
        onSelection={(event) => {
          if (event.length > 0 && event[0].rows) {
            selectionRef.current = true;
            markerSelection.select(rows[event[0].rows[0]].uid);
          }
        }}
        selectionModes={[RegionCardinality.CELLS, RegionCardinality.FULL_ROWS]}
        cellRendererDependencies={[checked, rows]}
      >
        <Column
          key="action"
          columnHeaderCellRenderer={headerCheckboxRenderer}
          cellRenderer={actionRenderer}
        />
        <Column
          key="name"
          columnHeaderCellRenderer={headerCellRenderer("Name")}
          cellRenderer={cellRenderer("name")}
        />
        <Column
          key="status"
          columnHeaderCellRenderer={headerCellRenderer("Status")}
          cellRenderer={statusRenderer}
        />
      </Table>
    </div>
  );
};

const useMarkerSelection = () => {
  const [selected, setSelected] = React.useState<any>(null);

  return {
    isSelected: (id) => {
      return selected === id;
    },
    select: (id) => {
      setSelected(id);
    },
    reset: () => {
      setSelected(null);
    },
  };
};

const ImageList = ({
  survey,
  images,
  removeImages,
  uploadInProgress,
  center,
  setCenter,
  zoom,
  setZoom,
}) => {
  const { sink } = useSinkState();
  const [currentTab, setCurrentTab] = React.useState("all");
  const markerSelection = useMarkerSelection();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [checked, setChecked] = React.useState({});

  const location = sink && sink.organization && sink.organization.location;

  const organizationLatitude = (location && location.coordinates[1]) || 49;
  const organizationLongitude = (location && location.coordinates[0]) || -125;

  const { complete, missing } = React.useMemo(() => {
    const complete = [];
    const missing = [];

    for (const image of images) {
      if (image.id && image.handle === undefined && image.uploaded === false) {
        missing.push(image);
      }

      if (image.uploaded === true) {
        complete.push(image);
      }
    }

    return { complete, missing };
  }, [images]);

  const filter = (images) =>
    images.filter((image) =>
      image.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const panel = (images) => {
    const filtered = filter(images);

    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            width: 100%;
            height: 400px;
          `}
        >
          {filtered.length === 0 ? (
            <NonIdealState
              icon="folder-open"
              description={
                images.length === 0
                  ? "There are currently no images added to the survey."
                  : "No images match that search term."
              }
              title={
                images.length === 0
                  ? "No images uploaded"
                  : "No matching images"
              }
            />
          ) : (
            <FileTable
              rows={filtered}
              checked={checked}
              setChecked={setChecked}
              markerSelection={markerSelection}
              removeImages={removeImages}
              uploadInProgress={uploadInProgress}
            />
          )}
          <MapPreview
            survey={survey}
            images={filtered}
            center={center}
            setCenter={setCenter}
            zoom={zoom}
            setZoom={setZoom}
            markerSelection={markerSelection}
            markerDeletion={checked}
            organizationLongitude={organizationLongitude}
            organizationLatitude={organizationLatitude}
          />
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    if (missing.length === 0 && currentTab === "missing") {
      setCurrentTab("all");
    }

    if (complete.length === 0 && currentTab === "complete") {
      setCurrentTab("all");
    }
  }, [missing, complete, currentTab]);

  React.useEffect(() => {
    if (uploadInProgress) {
      setChecked({});
    }
  }, [uploadInProgress]);

  return (
    <Tabs
      id="FileTabs"
      renderActiveTabPanelOnly
      selectedTabId={currentTab}
      onChange={(tabId: string) => {
        setCurrentTab(tabId);
        markerSelection.reset();
      }}
    >
      <Tab key="all" id="all" title="All" panel={panel(images)} />
      {complete.length > 0 && (
        <Tab
          key="complete"
          id="complete"
          title="Completed"
          panel={panel(complete)}
        />
      )}
      {missing.length > 0 && (
        <Tab
          key="missing"
          id="missing"
          title="Missing"
          panel={panel(missing)}
        />
      )}
      <InputGroup
        type="text"
        placeholder="Search..."
        fill
        onChange={(event) => setSearchTerm(event.target.value)}
      />
    </Tabs>
  );
};

export default React.memo(ImageList);
